import { LinearGradient } from 'expo-linear-gradient';
import React from "react";
import {
  View,
  Text,
  Image,
  StyleSheet,
  ViewStyle,
  TextStyle,
} from "react-native";

import { colors, spacing, typography } from "../styles/theme";

interface PlayerAvatarProps {
  nickname: string;
  size?: "sm" | "md" | "lg" | number; // Allow numeric sizes
  profilePictureUrl?: string;
  score?: number;
  isCurrentPlayer?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

const PlayerAvatar: React.FC<PlayerAvatarProps> = ({
  nickname,
  size = "md",
  profilePictureUrl,
  score,
  isCurrentPlayer = false,
  style,
  textStyle,
}) => {
  // Get initials from nickname (first 2 characters)
  const getInitials = (name: string | null | undefined): string => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Generate premium gradient colors from the nickname
  const stringToPremiumColors = (str: string | null | undefined): { primary: string; secondary: string } => {
    if (typeof str !== "string" || !str.length) {
      return { primary: '#9CA3AF', secondary: '#6B7280' };
    }
    
    // Premium color palette for avatars
    const premiumPalettes = [
      { primary: '#7C3AED', secondary: '#5B21B6' }, // Purple
      { primary: '#059669', secondary: '#047857' }, // Emerald
      { primary: '#DC2626', secondary: '#B91C1C' }, // Red
      { primary: '#EA580C', secondary: '#C2410C' }, // Orange
      { primary: '#0284C7', secondary: '#0369A1' }, // Blue
      { primary: '#7C2D12', secondary: '#92400E' }, // Brown
      { primary: '#BE185D', secondary: '#9D174D' }, // Pink
      { primary: '#4338CA', secondary: '#3730A3' }, // Indigo
    ];
    
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const paletteIndex = Math.abs(hash) % premiumPalettes.length;
    return premiumPalettes[paletteIndex];
  };

  // Size variants
  const sizeMap = {
    sm: {
      container: 32,
      text: typography.caption.fontSize,
      scoreSize: 16,
    },
    md: {
      container: 48,
      text: typography.h6.fontSize,
      scoreSize: 20,
    },
    lg: {
      container: 64,
      text: typography.h4.fontSize,
      scoreSize: 24,
    },
  };

  // Handle numeric sizes
  if (typeof size === "number") {
    // For numeric sizes, calculate proportional text and score sizes
    const baseSize = size;
    const sizeRatio = baseSize / 48; // Use 'md' as the base size

    const containerSize = baseSize;
    const textSize = (typography.h6.fontSize || 16) * sizeRatio;
    const scoreSize = 20 * sizeRatio;

    // Use these calculated values directly
    const premiumColors = stringToPremiumColors(nickname);
    const initials = getInitials(nickname);

    // Rest of the component will use these calculated values
    return (
      <View style={[styles.container, style]} data-oid="pnp_56a">
        <LinearGradient
          colors={[premiumColors.primary, premiumColors.secondary]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[
            styles.avatar,
            {
              width: containerSize,
              height: containerSize,
              borderRadius: containerSize / 2,
              borderWidth: isCurrentPlayer ? 3 : 0,
              borderColor: isCurrentPlayer ? '#FFFFFF' : "transparent",
              justifyContent: "center",
              alignItems: "center",
              shadowColor: 'rgba(0, 0, 0, 0.25)',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 1,
              shadowRadius: 4,
              elevation: 4,
            },
          ]}
          data-oid="5e.xsza"
        >
          <Text
            style={[
              styles.initials,
              {
                fontSize: textSize * 0.6, // Slightly smaller than container
                color: '#FFFFFF',
                fontWeight: '700',
                textShadowColor: 'rgba(0, 0, 0, 0.3)',
                textShadowOffset: { width: 0, height: 1 },
                textShadowRadius: 2,
              },
              textStyle,
            ]}
            numberOfLines={1}
            adjustsFontSizeToFit
            data-oid="g_3gnax"
          >
            {initials}
          </Text>
        </LinearGradient>
        {score !== undefined && (
          <Text
            style={[
              styles.score,
              {
                fontSize: scoreSize * 0.8,
                lineHeight: scoreSize,
              },
            ]}
            data-oid="tyah90q"
          >
            {score}
          </Text>
        )}
      </View>
    );
  }

  // Handle string sizes ('sm', 'md', 'lg')
  const validSizes = ["sm", "md", "lg"] as const;
  const safeSize = validSizes.includes(size as any) ? size : "md";
  if (!sizeMap[size as keyof typeof sizeMap]) {
    console.warn(
      `[PlayerAvatar] Invalid size '${size}' provided. Falling back to 'md'.`,
    );
  }
  const {
    container: containerSize,
    text: textSize,
    scoreSize,
  } = sizeMap[safeSize];
  const premiumColors = stringToPremiumColors(nickname);
  const initials = getInitials(nickname);

  // Render profile picture if available
  if (profilePictureUrl) {
    return (
      <View style={[styles.container, style]} data-oid="y5moa-w">
        <View
          style={[
            styles.avatar,
            {
              width: containerSize,
              height: containerSize,
              borderRadius: containerSize / 2,
              borderWidth: isCurrentPlayer ? 2 : 0,
              borderColor: isCurrentPlayer ? colors.primary : "transparent",
              backgroundColor: colors.background,
            },
          ]}
          data-oid="ty8..xj"
        >
          <Image
            source={{ uri: profilePictureUrl }}
            style={{
              width: containerSize,
              height: containerSize,
              borderRadius: containerSize / 2,
            }}
            resizeMode="cover"
            accessibilityLabel={`Profile picture of ${nickname}`}
            data-oid="3i2fjx2"
          />
        </View>
        {score !== undefined && (
          <View
            style={[
              styles.scoreContainer,
              { width: scoreSize, height: scoreSize },
            ]}
            data-oid="xin5x43"
          >
            <Text
              style={[styles.scoreText, { fontSize: scoreSize * 0.6 }]}
              data-oid="545ki:y"
            >
              {score}
            </Text>
          </View>
        )}
      </View>
    );
  }

  // Fallback to initials avatar
  return (
    <View style={[styles.container, style]} data-oid="0eos0kh">
      <LinearGradient
        colors={[premiumColors.primary, premiumColors.secondary]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={[
          styles.avatar,
          {
            width: containerSize,
            height: containerSize,
            borderRadius: containerSize / 2,
            borderWidth: isCurrentPlayer ? 3 : 0,
            borderColor: isCurrentPlayer ? '#FFFFFF' : "transparent",
            shadowColor: 'rgba(0, 0, 0, 0.25)',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 1,
            shadowRadius: 4,
            elevation: 4,
          },
        ]}
        data-oid="0355dzs"
      >
        <Text
          style={[
            styles.initials,
            {
              fontSize: textSize,
              color: '#FFFFFF',
              fontWeight: '700',
              textShadowColor: 'rgba(0, 0, 0, 0.3)',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 2,
              ...textStyle,
            },
          ]}
          accessibilityLabel={`Avatar for ${nickname}`}
          data-oid="qz14ldi"
        >
          {initials}
        </Text>
      </LinearGradient>
      {score !== undefined && (
        <View
          style={[
            styles.scoreContainer,
            { width: scoreSize, height: scoreSize },
          ]}
          data-oid=".w_h_di"
        >
          <Text
            style={[styles.scoreText, { fontSize: scoreSize * 0.6 }]}
            data-oid="2_xszoz"
          >
            {score}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  avatar: {
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  initials: {
    fontWeight: "bold",
    textAlign: "center",
  },
  score: {
    position: "absolute",
    bottom: -8,
    right: -8,
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    textAlign: "center",
    lineHeight: 24,
    fontSize: 12,
    fontWeight: "bold",
    overflow: "hidden",
  },
  scoreContainer: {
    position: "absolute",
    bottom: -spacing.xs,
    right: -spacing.xs,
    backgroundColor: colors.secondary,
    borderRadius: 50,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: colors.background,
  },
  scoreText: {
    color: colors.onSecondary,
    fontWeight: "bold",
    textAlign: "center",
  },
});

export default PlayerAvatar;
